import {
  ArrowUpTrayIcon,
  EnvelopeIcon,
  LockOpenIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import PaintPourIcon from '../icons/paint-pour.svg?react';
import PaletteIcon from '../icons/palette.svg?react';
import { ogImage } from '../../utility/head';
import { KB_VARIANTS, type KbVariantType } from './kb/variants';

/**
 * Copy for the paid /kb landing pages. Each variant's JSON is self-contained, so
 * any of them can be rewritten on its own without touching the others. Globbed
 * eagerly, so the copy is bundled at build time and the pages prerender.
 */

export { KB_VARIANTS, type KbVariantType };

const ICON_MAP = {
  'arrow-up-tray': ArrowUpTrayIcon,
  envelope: EnvelopeIcon,
  'lock-open': LockOpenIcon,
  'paint-pour': PaintPourIcon,
  palette: PaletteIcon,
  'user-group': UserGroupIcon,
} as const;

type IconKeyType = keyof typeof ICON_MAP;

export type IconComponentType = (typeof ICON_MAP)[IconKeyType];

interface RawBulletInterface {
  icon: string;
  text: string;
}

export interface BulletInterface {
  Icon: IconComponentType;
  text: string;
}

export interface FaqInterface {
  q: string;
  a: string;
}

interface RawKbInterface {
  seo: { title: string; description: string; image: string; imageSizes: string };
  stickyCta: string;
  hero: {
    badge: string;
    headline: string;
    headlineHighlight: string;
    subhead: string;
    proofChips: string[];
    primaryCta: string;
    imageAlt: string;
  };
  trustedBy: { eyebrow: string };
  features: { eyebrow: string; heading: string; intro: string };
  accessControls: {
    eyebrow: string;
    heading: string;
    body: string;
    bullets: RawBulletInterface[];
    linkLabel: string;
    imageAlt: string;
  };
  branding: {
    eyebrow: string;
    heading: string;
    body: string;
    bullets: RawBulletInterface[];
    imageAlt: string;
  };
  howItWorks: {
    eyebrow: string;
    heading: string;
    steps: Array<{ title: string; body: string; time: string }>;
  };
  pricing: { eyebrow: string; heading: string; intro: string };
  faqsHeading: string;
  faqs: FaqInterface[];
  closingBody: string;
}

// JSON carries icons as names, so the lookup is genuinely unchecked.
const ICONS_BY_NAME: Record<string, IconComponentType | undefined> = ICON_MAP;

function resolveBullets(bullets: RawBulletInterface[], where: string): BulletInterface[] {
  return bullets.map(({ icon, text }) => {
    const Icon = ICONS_BY_NAME[icon];

    if (!Icon) throw new Error(`[kb] Unknown icon "${icon}" in ${where}. Add it to ICON_MAP.`);

    return { Icon, text };
  });
}

// Eager glob, matching useCases.ts.
const modules = import.meta.glob<{ default: unknown }>('./kb/*.json', {
  eager: true,
});

function copyFile(name: string): unknown {
  const module = modules[`./kb/${name}.json`];

  if (!module) throw new Error(`[kb] ./kb/${name}.json is missing`);

  return module.default;
}

function resolve(variant: KbVariantType) {
  const raw = copyFile(variant) as RawKbInterface;

  return {
    ...raw,
    accessControls: {
      ...raw.accessControls,
      bullets: resolveBullets(raw.accessControls.bullets, `${variant}.accessControls`),
    },
    branding: {
      ...raw.branding,
      bullets: resolveBullets(raw.branding.bullets, `${variant}.branding`),
    },
    seo: {
      title: raw.seo.title,
      description: raw.seo.description,
      // Social card only — not preloaded, since the hero is the rendered
      // CoverageDemo and this image never appears on the page.
      image: ogImage({ src: raw.seo.image }),
      schema: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: raw.faqs.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      },
    },
  };
}

export type KbContentType = ReturnType<typeof resolve>;

export const kbContent: Record<KbVariantType, KbContentType> = Object.fromEntries(
  KB_VARIANTS.map(variant => [variant, resolve(variant)])
) as Record<KbVariantType, KbContentType>;
